import { fileUrl, initials, label, money } from "../lib/api";
import { Inbox } from "lucide-react";

export const Avatar = ({ src, name, size = "h-9 w-9", className = "", ring = false }) => {
  const url = fileUrl(src);
  return url ? (
    <img src={url} alt={name || ""} className={`${size} rounded-full object-cover ${ring ? "ring-2 ring-white" : ""} ${className}`} />
  ) : (
    <span className={`${size} rounded-full bg-nini-mint text-nini-deep font-semibold inline-flex items-center justify-center text-xs ${ring ? "ring-2 ring-white" : ""} ${className}`}>
      {initials(name)}
    </span>
  );
};

export const AvatarStack = ({ people = [], max = 4, extra = 0 }) => {
  const shown = people.slice(0, max);
  const more = extra || Math.max(0, people.length - max);
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((p, i) => <Avatar key={p.id || i} src={p.avatar_url} name={p.name} size="h-7 w-7" ring />)}
      {more > 0 && <span className="h-7 w-7 rounded-full bg-nini-surface border border-white text-[11px] font-semibold text-nini-muted inline-flex items-center justify-center">+{more}</span>}
    </div>
  );
};

const STATUS_STYLES = {
  paid: "bg-nini-mint text-nini-deep border-[#CDE3CF]", verified: "bg-nini-mint text-nini-deep border-[#CDE3CF]", approved: "bg-nini-mint text-nini-deep border-[#CDE3CF]",
  active: "bg-nini-mint text-nini-deep border-[#CDE3CF]", completed: "bg-nini-mint text-nini-deep border-[#CDE3CF]", up_to_date: "bg-nini-mint text-nini-deep border-[#CDE3CF]",
  accepted: "bg-nini-mint text-nini-deep border-[#CDE3CF]",
  pending_verification: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]", pending: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]", pending_approval: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]",
  partially_paid: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]", ready: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]", awaiting_contributions: "bg-[#FFF8E6] text-[#B47D00] border-[#F3E2B4]",
  overdue: "bg-[#FDF2F2] text-[#C53030] border-[#F8C4C4]", rejected: "bg-[#FDF2F2] text-[#C53030] border-[#F8C4C4]", cancelled: "bg-[#FDF2F2] text-[#C53030] border-[#F8C4C4]",
  suspended: "bg-[#FDF2F2] text-[#C53030] border-[#F8C4C4]",
  outstanding: "bg-nini-surface text-nini-text border-nini-border", upcoming: "bg-nini-surface text-nini-text border-nini-border", scheduled: "bg-nini-surface text-nini-text border-nini-border",
  archived: "bg-nini-surface text-nini-muted border-nini-border", unpaid: "bg-nini-surface text-nini-text border-nini-border",
};

export const StatusBadge = ({ status, className = "" }) => (
  <span data-testid={`status-badge-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.outstanding} ${className}`}>
    {label(status)}
  </span>
);

export const RoleBadge = ({ role }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${["admin", "chairperson", "treasurer", "secretary"].includes(role) ? "bg-nini-mint text-nini-deep" : "bg-nini-surface text-nini-muted"}`}>{label(role)}</span>
);

export const Progress = ({ value = 0, className = "", tone = "green" }) => (
  <div className={`h-2 w-full rounded-full bg-nini-surface overflow-hidden ${className}`}>
    <div className={`h-full rounded-full transition-all duration-700 ${tone === "sage" ? "bg-nini-sage" : "bg-nini-green"}`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
  </div>
);

export const StatCard = ({ label: l, value, hint, icon: Icon, testId }) => (
  <div className="card p-5 md:p-6 flex flex-col gap-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-nini-muted font-medium">{l}</span>
      {Icon && <span className="h-9 w-9 rounded-xl bg-nini-mint text-nini-deep inline-flex items-center justify-center"><Icon size={18} /></span>}
    </div>
    <div className="text-2xl md:text-[28px] font-bold text-nini-heading tracking-tight">{value}</div>
    {hint && <div className="text-xs text-nini-muted">{hint}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-nini-heading tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-nini-muted mt-1">{subtitle}</p>}
    </div>
    {action && <div className="flex gap-2 flex-wrap">{action}</div>}
  </div>
);

export const EmptyState = ({ title, hint, action, icon: Icon = Inbox }) => (
  <div className="card p-10 text-center flex flex-col items-center gap-3" data-testid="empty-state">
    <span className="h-12 w-12 rounded-2xl bg-nini-surface text-nini-muted inline-flex items-center justify-center"><Icon size={22} /></span>
    <div className="font-semibold text-nini-heading">{title}</div>
    {hint && <p className="text-sm text-nini-muted max-w-sm">{hint}</p>}
    {action}
  </div>
);

export const Money = ({ value, className = "" }) => <span className={`font-semibold text-nini-heading tabular-nums ${className}`}>{money(value)}</span>;

export const Field = ({ label: l, children, hint, required }) => (
  <label className="block space-y-1.5">
    {l && <span className="text-sm font-medium text-nini-text">{l}{required && <span className="text-nini-green"> *</span>}</span>}
    {children}
    {hint && <span className="block text-xs text-nini-muted">{hint}</span>}
  </label>
);

export const Toggle = ({ checked, onChange, label: l, hint, testId }) => (
  <button type="button" data-testid={testId} onClick={() => onChange(!checked)} className="w-full flex items-center justify-between gap-4 p-4 rounded-xl border border-nini-border hover:bg-nini-surface/60 transition text-left">
    <div>
      <div className="text-sm font-medium text-nini-text">{l}</div>
      {hint && <div className="text-xs text-nini-muted mt-0.5">{hint}</div>}
    </div>
    <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-nini-green" : "bg-nini-border"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </span>
  </button>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-16" data-testid="loading-spinner">
    <div className="h-8 w-8 rounded-full border-[3px] border-nini-mint border-t-nini-green animate-spin" />
  </div>
);

export const Select = ({ value, onChange, options, testId, placeholder, className = "" }) => (
  <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`field appearance-none pr-9 bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%237A8087' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0.9rem_center] ${className}`}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, label(o)]; return <option key={v} value={v}>{l}</option>; })}
  </select>
);
